import { CellValue } from './types';
import { RunReading, RunSnapshot, printValue } from './runs';

/**
 * The procedure's own parameters as a report prints them: what it loops over
 * and the lists it carries (the snapshot's `parameters`), which make up its
 * test matrix — the temperatures and light functions it offers and which this
 * run ran, an over-current envelope region by region, each LED string's
 * fall-time limit.
 *
 * A choice with nothing but its names (20 power connections, one run) is a
 * line: what ran and what else is offered. A choice or list whose entries carry
 * values is a table, one row per entry; a choice's table marks the entries
 * this run ran. A single value is a key and its value. Files and the DUT's
 * own details print elsewhere.
 */

export interface MatrixLine {
  key: string;
  title: string;
  inRun: string[];
  offered: string[];
}

export interface MatrixTable {
  key: string;
  title: string;
  columns: Array<{ key: string; label: string }>;
  rows: Array<{ key: string; label: string; cells: Record<string, string> }>;
  /** a choice's other entries, not run here; empty for a list */
  offered: string[];
  choice: boolean;
}

export interface ProcedureParameters {
  lines: MatrixLine[];
  tables: MatrixTable[];
  values: Array<{ label: string; value: string }>;
}

const LABEL_KEYS = new Set(['title', 'name', 'label']);
const columnLabel = (key: string) => {
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
const text = (v: CellValue) => printValue(v).trim();
const isFile = (r: RunReading) => (r.attachments ?? []).length > 0 || r.files.length > 0;

export function procedureParameters(run: Pick<RunSnapshot, 'parameters'>): ProcedureParameters {
  const out: ProcedureParameters = { lines: [], tables: [], values: [] };
  for (const p of run.parameters ?? []) {
    if (isFile(p) || p.identifier.toLowerCase() === 'dut') continue;
    const labels = p.values.map(text);
    const choice = Array.isArray(p.choices);
    const offered = choice ? (p.choices ?? []).map(text).filter((c) => c && !labels.includes(c)) : [];
    const keys = Array.from(new Set(p.records.flatMap((r) => Object.keys(r)))).filter((k) => !LABEL_KEYS.has(k) && p.records.some((r) => text(r[k] ?? null) !== ''));
    const title = p.title || columnLabel(p.identifier);
    if (keys.length > 0) {
      out.tables.push({
        key: p.parameterId,
        title,
        columns: keys.map((k) => ({ key: k, label: columnLabel(k) })),
        rows: p.records.map((r, i) => ({ key: `${p.parameterId}-${i}`, label: labels[i] || text(r.title ?? r.name ?? null) || `${i + 1}`, cells: Object.fromEntries(keys.map((k) => [k, text(r[k] ?? null)])) })),
        offered,
        choice,
      });
    } else if (choice || labels.length > 1) {
      if (labels.length || offered.length) out.lines.push({ key: p.parameterId, title, inRun: labels, offered });
    } else if (labels[0]) {
      out.values.push({ label: title, value: labels[0] });
    }
  }
  return out;
}

export const isEmptyProcedureParameters = (p: ProcedureParameters): boolean => p.lines.length + p.tables.length + p.values.length === 0;
